import { Alert } from "@hakistudio/hakiui";
import { Sparkles } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/api";
import { authClient } from "@/lib/auth";
import { useNotes, useProfile } from "@/lib/queries";

/**
 * The consent screen for the remote MCP connector.
 *
 * Claude's cloud sends the browser to the Worker's `/authorize`, which bounces
 * here with the original OAuth query string intact. This page exists so the
 * approval is made by a signed-in user in the app they already trust: the
 * Worker issues the grant from the bearer token this page sends, not from
 * anything in the URL, so a crafted link cannot approve itself.
 */
export function OAuthConsentPage() {
  const { data: profile } = useProfile();
  const { data: session } = authClient.useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Shown so it is obvious *whose* notes are about to be connected.
  // One row is enough to answer the only question that matters here: does this
  // account actually have anything to connect?
  const { data: page } = useNotes({});
  const isEmpty = page ? page.notes.length === 0 : undefined;

  const params = window.location.search.replace(/^\?/, "");
  const clientName = new URLSearchParams(params).get("client_name") ?? "An application";

  const approve = async () => {
    setBusy(true);
    setError(null);
    try {
      const { redirectTo } = await api<{ redirectTo: string }>("/v1/mcp/oauth/approve", {
        method: "POST",
        body: JSON.stringify({ params }),
      });
      // Hand control back to Claude with the authorization code.
      window.location.replace(redirectTo);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 p-6">
      <div className="flex flex-col gap-4 rounded-card border border-line bg-raised p-6">
        <div className="flex items-start gap-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-sunken text-ink-muted">
            <Sparkles className="h-5 w-5" />
          </span>
          <div className="flex min-w-0 flex-col">
            <h1 className="text-lg font-semibold">Connect {clientName}?</h1>
            <p className="text-sm text-ink-muted">
              It will be able to read and write your notes, tasks, calendar and fridge.
            </p>
          </div>
        </div>

        {/* The email, not just the display name. Several Sebas accounts can
            share a display name, and approving as the wrong one connects an
            empty set of notes with no visible sign anything went wrong — which
            is exactly what happened the first time this shipped. */}
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 border-t border-line pt-4 text-sm">
          <dt className="text-ink-faint">Account</dt>
          <dd className="truncate font-medium">{session?.user?.email ?? profile?.displayName ?? "…"}</dd>
          <dt className="text-ink-faint">Notes</dt>
          <dd className="truncate text-ink-muted">
            {isEmpty === undefined ? "…" : isEmpty ? "none" : "found"}
          </dd>
        </dl>

        {isEmpty ? (
          <Alert variant="warning" title="This account has no notes">
            Connecting it will give Claude nothing to read. If you meant a different
            account, sign out and sign back in before approving.
          </Alert>
        ) : null}

        {error ? <Alert variant="danger" title="Could not approve">{error}</Alert> : null}

        <div className="flex gap-2">
          <Button onClick={() => void approve()} disabled={busy || !profile}>
            {busy ? "Connecting…" : "Approve"}
          </Button>
          <Button variant="secondary" onClick={() => window.history.back()} disabled={busy}>
            Cancel
          </Button>
        </div>

        <p className="text-xs text-ink-faint">
          You can revoke this at any time from Settings.
        </p>
      </div>
    </div>
  );
}
